import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import * as LucideIcons from "lucide-react";

// This is a local, robust implementation of the Iconly icons using Lucide as a reliable backend.
// This avoids the jsx-runtime mismatch and ensures icons load correctly on all devices.

export type IconlyName = 
  | "Document" | "User" | "Wallet" | "TickSquare" | "Plus" | "Paper" 
  | "Work" | "Calendar" | "Call" | "ShieldDone" | "Category" | "Lock" 
  | "Location" | "ArrowRight" | "Home" | "Search" | "Notification"
  | "Library" | "CaretLeft" | "Star" | "ArrowLeft" | "Video" | "Send" | "Discovery";

interface IconlyWrapperProps {
  name: IconlyName | string;
  set?: "bold" | "bulk" | "light" | "broken" | "two-tone" | "curved";
  size?: "small" | "medium" | "large" | "xlarge" | number;
  primaryColor?: string;
  secondaryColor?: string;
  stroke?: "light" | "regular" | "bold";
  className?: string;
  animate?: boolean;
}

const sizeMap = {
  small: 16,
  medium: 24,
  large: 32,
  xlarge: 48,
};

// Map Iconly names to Lucide icons
const iconMap: Record<string, any> = {
  Document: LucideIcons.FileText,
  User: LucideIcons.User,
  Wallet: LucideIcons.Wallet,
  TickSquare: LucideIcons.CheckSquare,
  Plus: LucideIcons.Plus,
  Paper: LucideIcons.File,
  Work: LucideIcons.Briefcase,
  Calendar: LucideIcons.Calendar,
  Call: LucideIcons.Phone,
  ShieldDone: LucideIcons.ShieldCheck,
  Category: LucideIcons.LayoutGrid,
  Lock: LucideIcons.Lock,
  Location: LucideIcons.MapPin,
  ArrowRight: LucideIcons.ArrowRight,
  Home: LucideIcons.Home,
  Search: LucideIcons.Search,
  Notification: LucideIcons.Bell,
  Library: LucideIcons.Library,
  CaretLeft: LucideIcons.ChevronLeft,
  Star: LucideIcons.Star,
  ArrowLeft: LucideIcons.ArrowLeft,
  Video: LucideIcons.Video,
  Send: LucideIcons.Send,
  Discovery: LucideIcons.Compass,
};

export function Iconly({
  name,
  set = "light",
  size = "medium",
  primaryColor,
  secondaryColor,
  stroke = "regular",
  className,
  animate = false,
}: IconlyWrapperProps) {
  
  const LucideIcon = useMemo(() => {
    // Try exact match first
    if (iconMap[name]) return iconMap[name];
    
    // Try to find a Lucide icon that matches the name if it's not in our map
    const potentialKey = name.charAt(0).toUpperCase() + name.slice(1);
    return (LucideIcons as any)[potentialKey] || (LucideIcons as any)[name] || null;
  }, [name]);

  if (!LucideIcon) {
    console.warn(`[Iconly] Missing icon mapping for: ${name}`);
    // Graceful fallback: show a circle so the UI doesn't look empty
    return (
      <div 
        className={cn("flex items-center justify-center rounded-full bg-slate-100", className)} 
        style={{ 
          width: typeof size === 'number' ? size : sizeMap[size as keyof typeof sizeMap] || 24,
          height: typeof size === 'number' ? size : sizeMap[size as keyof typeof sizeMap] || 24,
        }}
      >
        <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
      </div>
    );
  }

  const numSize = typeof size === 'number' ? size : sizeMap[size as keyof typeof sizeMap] || 24;
  const isBold = set === "bold" || set === "bulk" || set === "curved";
  // We use a slightly thinner stroke for the outline to keep it elegant
  const strokeWidth = isBold ? 2.2 : 1.6;

  const content = (
    <div className="relative flex items-center justify-center">
      {/* Soft Background Fill for premium 'Bulk' depth */}
      {isBold && (
        <LucideIcon
          size={numSize}
          strokeWidth={0}
          className="absolute inset-0 opacity-[0.18] fill-current"
          style={{ color: primaryColor || "currentColor" }}
        />
      )}
      <LucideIcon
        size={numSize}
        color={primaryColor || "currentColor"}
        strokeWidth={strokeWidth}
        className="relative transition-all duration-300 fill-none"
      />
    </div>
  );

  if (animate) {
    return (
      <motion.span 
        className={cn("inline-flex items-center justify-center", className)}
        whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
        whileTap={{ scale: 0.9 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
      >
        {content}
      </motion.span>
    );
  }

  return (
    <span className={cn("inline-flex items-center justify-center", className)}>
      {content}
    </span>
  );
}
